import {
  WizardAICard,
  WizardCard,
  WizardClassificationCard,
  WizardClusterCard,
  WizardDateMetricCard,
  WizardDateTermCard,
  WizardDemographicCard,
  WizardGeoCard,
  WizardPivotCard,
  WizardPostsCard,
  WizardTermCard,
  WizardTermMetricsCard,
  WizardUsersCard,
} from "./model/card";
import { WizardConfigurationConditionField } from "./model/section";

function getWizardCardFromType<WC extends WizardCard, T extends keyof WC>(
  type: WC["type"]
) {
  return function getWizardCard(
    card: Pick<WC, T | "title" | "dataviz"> &
      Partial<Omit<WC, T | "type" | "dataviz">>,
    clause?: WizardConfigurationConditionField
  ) {
    return {
      type: type as WC["type"],
      ...card,
      wizardClause: clause,
    } as WC;
  };
}

export function isWizardDateMetricCard(
  card: WizardCard
): card is WizardDateMetricCard {
  return card.type === "date-metric";
}

export function isWizardPostsCard(card: WizardCard): card is WizardPostsCard {
  return card.type === "posts";
}

export function isWizardTermCard(card: WizardCard): card is WizardTermCard {
  return card.type === "term";
}

export function isWizardPivotCard(card: WizardCard): card is WizardPivotCard {
  return card.type === "pivot";
}

export function isWizardUsersCard(card: WizardCard): card is WizardUsersCard {
  return card.type === "users";
}

export function isWizardGeoCard(card: WizardCard): card is WizardGeoCard {
  return card.type === "geo";
}

export function isWizardTermMetricsCard(
  card: WizardCard
): card is WizardTermMetricsCard {
  return card.type === "term-metrics";
}

export function isWizardDateTermCard(
  card: WizardCard
): card is WizardDateTermCard {
  return card.type === "date-term";
}

export function isWizardDemographicCard(
  card: WizardCard
): card is WizardDemographicCard {
  return card.type === "demographic";
}

export function isWizardClassificationCard(
  card: WizardCard
): card is WizardClassificationCard {
  return card.type === "classification";
}

export function isWizardAICard(card: WizardCard): card is WizardAICard {
  return card.type === "ai";
}

export function isWizardClusterCard(
  card: WizardCard
): card is WizardClusterCard {
  return card.type === "cluster";
}

export const getWizardDateMetricCard = getWizardCardFromType<
  WizardDateMetricCard,
  "metric"
>("date-metric");

export const getWizardPostsCard = getWizardCardFromType<
  WizardPostsCard,
  "sort"
>("posts");

export const getWizardTermCard = getWizardCardFromType<
  WizardTermCard,
  "metric" | "term"
>("term");

export const getWizardPivotCard = getWizardCardFromType<
  WizardPivotCard,
  "term" | "pivot"
>("pivot");

export const getWizardUsersCard = getWizardCardFromType<
  WizardUsersCard,
  "sort"
>("users");

export const getWizardGeoCard = getWizardCardFromType<
  WizardGeoCard,
  "term" | "metric"
>("geo");

export const getWizardDemographicCard = getWizardCardFromType<
  WizardDemographicCard,
  "metric"
>("demographic");

export const getWizardDateTermCard = getWizardCardFromType<
  WizardDateTermCard,
  "metric" | "term"
>("date-term");

export const getWizardTermMetricsCard = getWizardCardFromType<
  WizardTermMetricsCard,
  "metrics" | "term"
>("term-metrics");

export const getWizardClassificationCard = getWizardCardFromType<
  WizardClassificationCard,
  "metric"
>("classification");

export const getWizardAICard = getWizardCardFromType<WizardAICard, "analysis">(
  "ai"
);

export const getWizardClusterCard = getWizardCardFromType<
  WizardClusterCard,
  "metric"
>("cluster");
